import React, { useCallback, useEffect, useState } from 'react'
import { Formik, Form, Field,FieldArray  } from 'formik';
import { TextField } from 'formik-material-ui';

import { makeStyles } from "@material-ui/core/styles";
import LinearProgress from '@material-ui/core/LinearProgress';
import MenuItem from '@material-ui/core/MenuItem'
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';


import { Button, FormikOnChangeTextField } from "../../components/Wrappers";
import { useAddSnackbarQueue} from "../../context/SnackbarContext";
import { useCloseDialog } from "../../context/DialogContext";
import { useStoreHelpers } from '../../store'
import { useHelperStyles } from '../../styles'
import {useApiHttpState} from '../../context/ApiContext'
import {getFullName} from '../../utils'
import {transformNumberTo10DigitFormat} from '../../utils/common'
import { normalizeHousehold } from '../../utils/householdData'


const useStyles = makeStyles(theme => ({
    center: {
        margin : "0 auto"
    },
    twoThirdsWidth: {
        width: "66%"
    },
    listHeader: {
        color: "#fff",
        backgroundColor: "rgb(0 0 0 / 20%)"
    },
    matchesHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: theme.spacing(1, 2)
    }
}))

const HouseholdMatchLookup = ({ api, household, values, onResults }) => {
    const lookupCriteria = {
        familyName: (values.familyName || "").trim(),
        phoneNumbers: values.phoneNumbers.map(phone => typeof phone === "string" ? phone : phone.phoneNumber).filter(Boolean),
        emails: values.emails.map(email => typeof email === "string" ? email : email.email).filter(Boolean),
    }
    const lookupKey = JSON.stringify(lookupCriteria)

    useEffect(() => {
        if (household || !api || (!lookupCriteria.familyName && !lookupCriteria.phoneNumbers.length && !lookupCriteria.emails.length)) {
            onResults([])
            return
        }

        let current = true
        const timeoutId = setTimeout(async () => {
            const [error, response] = await api.get('/households/search', {
                params: {
                    familyName: lookupCriteria.familyName,
                    phoneNumbers: lookupCriteria.phoneNumbers.join(','),
                    emails: lookupCriteria.emails.join(','),
                }
            })
            if (!current) return
            if (error) { onResults([]); return }
            try {
                onResults(response.data.map(normalizeHousehold))
            } catch (parseError) {
                onResults([])
            }
        }, 300)

        return () => {
            current = false
            clearTimeout(timeoutId)
        }
    }, [api, household, lookupKey, onResults])

    return null
}

const UpsertForm = ({household, phoneNumber,mutate,history,prefill = {}, mode = "standard", mergeHousehold}) => {
    const isMerge = mode === "merge"
    const [similarHouseholds,setSimilarHouseholds] = useState([])
    const [matchesExpanded, setMatchesExpanded] = useState(true)
    const close = useCloseDialog() 
    const { api } = useApiHttpState()    
    const addSnackbarQueue = useAddSnackbarQueue()    
    const helperClasses = useHelperStyles() 
    const classes = useStyles()   
    const { getItemsArray : getItems , addItems , addItem } = useStoreHelpers()  
    
    const loadPhoneNumberTypes = async  () => {
        const [er,response] = await api.get('/phoneNumberTypes/',{useLoader:true})        
        if (!er) addItems(response.data,"phoneNumberTypes")        
    }
    useEffect(() => {
        if (!isMerge) loadPhoneNumberTypes()
    },[isMerge])
    const phoneNumberTypes = isMerge ? [] : getItems("phoneNumberTypes");
    const handleMatchResults = useCallback(matches => {
        setSimilarHouseholds(matches)
        if (matches.length) setMatchesExpanded(true)
    }, [])
    const openMatchingHousehold = householdId => {
        if (!history) return
        close()
        history.push(`/app/household/${householdId}`)
    }
    
    return (
        <Formik
            initialValues={{
                familyName: household ? household.familyName : prefill.familyName || '',
                husbandName: household ? household.husbandName : prefill.husbandName || '',
                wifeName: household ? household.wifeName : prefill.wifeName || '',
                address: household ? household.address : prefill.address || '',
                city: household ? household.city : prefill.city || '',
                state: household ? household.state : prefill.state || '',
                postalCode: household ? household.postalCode : prefill.postalCode || '',
                phoneNumbers: household && Array.isArray(household.phoneNumbers) && household.phoneNumbers.length ? household.phoneNumbers : [{phoneNumber:phoneNumber || prefill.phoneNumber || "",phoneTypeId:null}],
                emails: household && Array.isArray(household.emails) && household.emails.length ? household.emails : [{email:prefill.email || ""}]
            }}
            validate={values => {
                const errors = {};
                if (!values.familyName) {
                    errors.familyName = 'Required';
                } 
                //needs at least one phone number
                return errors;
            }}
            onSubmit={ async (values, { setSubmitting }) => {
                if (isMerge) {
                    const [er] = await api.post(`/households/${household.id}/merge`, {
                        mergeHouseholdId: mergeHousehold.id,
                        familyName: values.familyName,
                        husbandName: values.husbandName,
                        wifeName: values.wifeName,
                        address: values.address,
                        city: values.city,
                        state: values.state,
                        postalCode: values.postalCode,
                    })
                    setSubmitting(false)
                    if (!er) {
                        addSnackbarQueue("Households were merged")
                        close()
                        if (history) history.push(`/app/household/${household.id}`)
                    } else {
                        addSnackbarQueue("An error occurred while merging households", "error")
                    }
                    return
                }

                //might want to check here before submitting that there isn't a match. THe issue is if there is a false positive how to deal with it.
                const newValues = Object.assign({},values)

                newValues.phoneNumbers = newValues.phoneNumbers.map(value => {
                    const phoneNumber = {...value}
                    phoneNumber.phoneNumber =  transformNumberTo10DigitFormat(phoneNumber.phoneNumber)
                    delete phoneNumber.createdAt
                    delete phoneNumber.updatedAt
                    delete phoneNumber.phoneType
                    return phoneNumber
                }) .filter(phoneNumber => !!phoneNumber.phoneNumber)
                newValues.emails = newValues.emails.map(value => {
                    const email = {...value}
                    delete email.createdAt
                    delete email.updatedAt
                    return email
                }).filter(email => !!email.email)
                const [er,response] = household || newValues.id ? await api.put('/households/'+(household ? household.id : newValues.id), {...newValues} )  : await api.post('/households', { ...newValues } )
                setSubmitting(false);                
                if (!er) {
                    const savedHousehold = Array.isArray(response.data) ? response.data[0] : response.data
                    if(household && mutate) mutate()
                    //addItem(response.data,"households")
                    addSnackbarQueue("Household was " + (household ? "updated" : "created")) 
                    close()                  
                    //go to houshold page if not already there 
                    if(!household && history && savedHousehold?.id) history.push('/app/household/'+ savedHousehold.id)   
                } else {
                    addSnackbarQueue("An error occurred","error")
                }
                return;
            }}    
        >
        {({ submitForm,values,setValues, isSubmitting }) => {
            return (           
            <Form>
                {!isMerge && <HouseholdMatchLookup
                    api={api}
                    household={household}
                    values={values}
                    onResults={handleMatchResults}
                />}
                <Field
                    component={FormikOnChangeTextField}
                    name="familyName"
                    type="text"
                    label="Family Name"
                    variant="outlined"
                    size="small"
                    margin="normal"
                    className={helperClasses.marginRight}
                />                  
                <Field
                    component={FormikOnChangeTextField}
                    name="husbandName"
                    type="text"
                    label="Husband's Name"
                    variant="outlined"
                    margin="normal"
                    size="small"
                    className={helperClasses.marginRight}
                />    
                <Field
                    component={FormikOnChangeTextField}
                    name="wifeName"
                    type="text"
                    label="Wife's Name"
                    variant="outlined"
                    margin="normal"
                    size="small"
                    className={helperClasses.marginRight}
                />
                {!isMerge && !!similarHouseholds.length && !values.id && 
                    <Paper elevation={3} className={classes.center + " " + classes.twoThirdsWidth}>
                        <div className={classes.matchesHeader}>
                            <strong>Possible matching households ({similarHouseholds.length})</strong>
                            <Button size="small" color="primary" onClick={() => setMatchesExpanded(expanded => !expanded)}>
                                {matchesExpanded ? "Hide matches" : "Show matches"}
                            </Button>
                        </div>
                        {matchesExpanded &&
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Household</TableCell>
                                        <TableCell>Address</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {similarHouseholds.map(match => (
                                        <TableRow key={match.id}>
                                            <TableCell>{getFullName(match)}</TableCell>
                                            <TableCell>{`${match.address || ""} ${match.city || ""}, ${match.state || ""} ${match.postalCode || ""}`}</TableCell>
                                            <TableCell align="right">
                                                <Button size="small" color="success" onClick={() => openMatchingHousehold(match.id)} disabled={!history}>
                                                    View household
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        }
                    </Paper>
                }  
                <br />
                <Field
                    component={TextField}
                    name="address"
                    type="text"
                    label="Street"
                    variant="outlined"
                    margin="normal"
                    size="small"
                    fullWidth
                    className={helperClasses.marginRight}
                />   
                <br />  
                <Field
                    component={TextField}
                    name="city"
                    type="text"
                    label="City"
                    variant="outlined"
                    margin="normal"
                    size="small"
                    className={helperClasses.marginRight}
                />    
                <Field
                    component={TextField}
                    name="state"
                    type="text"
                    label="State"
                    variant="outlined"
                    margin="normal"
                    size="small"
                    className={helperClasses.marginRight}
                />
                <Field
                    component={TextField}
                    name="postalCode"
                    type="text"
                    label="Postal Code"
                    variant="outlined"
                    margin="normal"
                    size="small"
                    className={helperClasses.marginRight}
                />   
                {!isMerge && <>
                <br />
                <div>Phone Numbers</div>
                <FieldArray 
                    name="phoneNumbers"
                    render={arrayHelpers => {
                        return values.phoneNumbers.map((phoneNumber, index) => (
                            <div key={index}>
                              <Field 
                                component={TextField}
                                name={`phoneNumbers.[${index}].phoneNumber`} 
                                label="Phone Number"
                                variant="outlined"
                                margin="normal"
                                size="small"
                                className={helperClasses.marginRight}
                              />
                              <Field
                                component={TextField}
                                select={true}       
                                name={`phoneNumbers.[${index}].phoneTypeId`}                    
                                label="Phone Type"                                
                                variant="outlined"
                                margin="normal"
                                size="small" 
                                className={helperClasses.marginRight}  
                                style={{width:"15em"}}                 
                                >
                                    <MenuItem key={0} value={null}>
                                        Phone Number Type
                                    </MenuItem> 
                                    {!!phoneNumberTypes && phoneNumberTypes.map((phoneNumberType) => (
                                            <MenuItem key={phoneNumberType.id} value={phoneNumberType.id}>
                                                {phoneNumberType.name}
                                            </MenuItem>
                                    ))}
                                </Field>
                              
                              <span style={{marginTop: "16px", marginBottom: "8px",display:"inline-flex"}}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    className={helperClasses.marginRight} 
                                    onClick={() => arrayHelpers.remove(index)} // remove a friend from the list
                                >
                                    -
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => arrayHelpers.insert(index + 1, {phoneNumber: "", phoneTypeId: null})}
                                >
                                    +
                                </Button>

                              </span>
                            </div>
                          ))
                       
                    }}
                />
                <div>Email</div>
                <FieldArray 
                    name="emails"
                    render={arrayHelpers => {
                        return values.emails.map((email, index) => (
                            <div key={index}>
                              <Field 
                                component={TextField}
                                name={`emails.[${index}].email`} 
                                label="Email Address"
                                variant="outlined"
                                margin="normal"
                                size="small"
                                className={helperClasses.marginRight}
                              />                                                            
                              <span style={{marginTop: "16px", marginBottom: "8px",display:"inline-flex"}}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    className={helperClasses.marginRight} 
                                    onClick={() => arrayHelpers.remove(index)} // remove a friend from the list
                                >
                                    -
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => arrayHelpers.insert(index + 1, {email: ""})}
                                >
                                    +
                                </Button>

                              </span>
                            </div>
                          ))
                       
                    }}
                />
                </>}
                {isSubmitting && <LinearProgress />}
                <br />
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    onClick={submitForm}
                >
                    {isMerge ? "Submit Merge" : "Submit"}
                </Button>
            </Form>
        )}
        }
    </Formik>
    )
}

export default UpsertForm